import { useContext, useEffect, useRef, useState } from "react";
import { Button, Form, OverlayTrigger, Tooltip, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiDownload, BiPencil, BiUpload } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../App";
import { LS_AUTHORITY_KEY } from "../../../core/constants/common";
import useHasPermission from "../../../core/hooks/useHasPermission";
import UOMService from "../../../core/service/UOMService";
import UomFormModal from "./UomFormModal";

const UomContainer = ({ t }) => {
	const { setLoaderDisplay } = useContext(LoaderContext);
	const navigate = useNavigate();
	const fileInputRef = useRef(null);
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY) || "null");
	const { isSuperAdmin } = useHasPermission();
	const getCompanyId = () => {
		if (isSuperAdmin && isSuperAdmin()) {
			return null;
		}
		return lsData?.profile?.company?.id || null;
	};
	const companyId = getCompanyId();

	const [versions, setVersions] = useState([]);
	const [selectedVersion, setSelectedVersion] = useState("");
	const [searchKey, setSearchKey] = useState("");
	const [rows, setRows] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [isAdd, setIsAdd] = useState(true);
	const emptyForm = {
		inputUnitName: "",
		inputUnit: "",
		outputUnitName: "",
		outputUnit: "",
		multiple: "",
		geography: ""
	};
	const [form, setForm] = useState({ ...emptyForm });
	const [errors, setErrors] = useState({});
	const [editId, setEditId] = useState(null);

	useEffect(() => {
		loadVersions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadVersions = () => {
		setLoaderDisplay(true);
		UOMService.listVersions(companyId)
			.then((res) => {
				if (res.status === 200) {
					const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
					setVersions(list);
					if (list.length > 0) {
						const first = list[0].versionNumber;
						setSelectedVersion(first);
						loadTable(first);
					}
				}
			})
			.catch(() => {
				setVersions([]);
			})
			.finally(() => setLoaderDisplay(false));
	};

	const loadTable = (versionIdOrNum) => {
		if (!versionIdOrNum) return;
		setLoaderDisplay(true);
		UOMService.getConversionTableByVersion(versionIdOrNum, (companyId ?? 0))
			.then((res) => {
				if (res.status === 200) {
					const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
					setRows(data);
				} else {
					setRows([]);
				}
			})
			.catch(() => setRows([]))
			.finally(() => setLoaderDisplay(false));
	};

	const openAddModal = () => {
		setIsAdd(true);
		setEditId(null);
		setForm({ ...emptyForm });
		setErrors({});
		setShowModal(true);
	};

	const openEditModal = (row) => {
		setIsAdd(false);
		setEditId(row.id);
		setForm({
			inputUnitName: row.inputUnitName || row.inputUnit || "",
			inputUnit: row.inputUnit || "",
			outputUnitName: row.outputUnitName || row.outputUnit || "",
			outputUnit: row.outputUnit || "",
			multiple: row.multiple ?? "",
			geography: row.geography || ""
		});
		setErrors({});
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
		setForm({ ...emptyForm });
		setErrors({});
		setEditId(null);
	};

	const onChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((e) => ({ ...e, [field]: "" }));
		}
	};

	const validate = () => {
		const e = {};
		if (!form.inputUnitName?.trim()) e.inputUnitName = "required";
		if (!form.inputUnit?.trim()) e.inputUnit = "required";
		if (!form.outputUnitName?.trim()) e.outputUnitName = "required";
		if (!form.outputUnit?.trim()) e.outputUnit = "required";
		if (form.multiple === "" || isNaN(Number(form.multiple)) || Number(form.multiple) <= 0) e.multiple = "invalid";
		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const saveRow = () => {
		if (!selectedVersion) return;
		if (!validate()) return;
		const payload = {
			inputUnit: form.inputUnit.trim(),
			outputUnit: form.outputUnit.trim(),
			inputUnitName: form.inputUnitName.trim(),
			outputUnitName: form.outputUnitName.trim(),
			multiple: Number(form.multiple),
			geography: form.geography?.trim() || ""
		};
		setLoaderDisplay(true);
		const req = isAdd
			? UOMService.createConversion(selectedVersion, payload, (companyId ?? 0))
			: UOMService.updateConversion(editId, payload);
		req
			.then((res) => {
				if (res.status === 200 || res.status === 201) {
					toast.success(t("Success.label") || "Success");
					closeModal();
					loadTable(selectedVersion);
				}
			})
			.catch(() => {})
			.finally(() => setLoaderDisplay(false));
	};

	const handleVersionChange = (value) => {
		setSelectedVersion(value);
		loadTable(value);
	};

	const handleExport = () => {
		if (!selectedVersion) return;
		setLoaderDisplay(true);
		UOMService.exportVersion(selectedVersion, companyId)
			.then((response) => {
				setLoaderDisplay(false);
				const filename = `uom_v${selectedVersion}.xlsx`;
				const blob = new Blob([response.data], { type: "application/octet-stream" });
				const fileURL = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = fileURL;
				link.download = filename;
				link.click();
				link.remove();
				window.URL.revokeObjectURL(fileURL);
			})
			.catch(() => setLoaderDisplay(false));
	};

	const handleImportClick = () => {
		if (!selectedVersion) return;
		fileInputRef.current?.click();
	};

	const handleFileSelect = (event) => {
		const file = event.target.files[0];
		if (!file) return;
		const validTypes = [".xlsx", ".xls"];
		const fileExtension = "." + file.name.split(".").pop().toLowerCase();
		if (!validTypes.includes(fileExtension)) {
			toast.error(t("GHGDatasetSelection.import.invalidFileType.label"));
			fileInputRef.current.value = null;
			return;
		}
		const maxSize = 10 * 1024 * 1024;
		if (file.size > maxSize) {
			toast.error(t("GHGDatasetSelection.import.fileTooLarge.label"));
			fileInputRef.current.value = null;
			return;
		}
		importFile(file);
	};

	const importFile = (file) => {
		if (!selectedVersion) return;
		const formData = new FormData();
		formData.append("file", file);
		setLoaderDisplay(true);
		UOMService.importVersion(selectedVersion, formData, companyId)
			.then((response) => {
				setLoaderDisplay(false);
				fileInputRef.current.value = null;
				if (response.status === 200 || response.status === 201) {
					const resp = response?.data || {};
					const imported = Number(resp.importedRowsCount ?? 0);
					const skipped = Number(resp.skippedRowsCount ?? 0);
					const msg =
						t("Uom.import.result.label", { imported, skipped });
					toast.success(msg);
					loadTable(selectedVersion);
				}
			})
			.catch(() => {
				setLoaderDisplay(false);
				fileInputRef.current.value = null;
			});
	};

	const filteredRows = (rows || []).filter((r) => {
		if (!searchKey) return true;
		const key = searchKey.toLowerCase();
		return (
			(r.inputUnitName || "").toLowerCase().includes(key) ||
			(r.inputUnit || "").toLowerCase().includes(key) ||
			(r.outputUnitName || "").toLowerCase().includes(key) ||
			(r.outputUnit || "").toLowerCase().includes(key) ||
			String(r.multiple ?? "").toLowerCase().includes(key) ||
			(r.geography || "").toLowerCase().includes(key)
		);
	});

	return (
		<div className="container" data-testid="uom-container">
			<div className="mb-3">
				<h5 className="MH mb-0 text-start">{t("ConversionFactor.label")}</h5>
			</div>

			<div className="d-flex justify-content-between align-items-center mb-3">
				<div className="d-flex align-items-center">
					<Form.Control
						type="text"
						placeholder={t("GHGEmissionSources.filters.name.placeholder.label")}
						value={searchKey}
						onChange={(e) => setSearchKey(e.target.value)}
						style={{ width: "auto", minWidth: "200px" }}
						data-testid="search-input"
					/>
				</div>
				<div className="d-flex align-items-center gap-2">
					<Form.Select
						value={selectedVersion}
						onChange={(e) => handleVersionChange(e.target.value)}
						style={{ width: "auto", maxWidth: "200px" }}
						className="d-inline-block"
						data-testid="version-selector"
					>
						<option value="">{t("GHGDatasetSelection.selectVersion.label")}</option>
						{(versions || []).map((v) => (
							<option key={v.id || v.versionNumber} value={v.versionNumber}>
								{v.versionName || `Version ${v.versionNumber}`}
							</option>
						))}
					</Form.Select>
					<Button
						variant="outline-secondary"
						onClick={() => navigate(companyId ? "/company_admin/uom/versions" : "/admin/uom/versions")}
						data-testid="manage-versions-button"
						disabled={false}
					>
						{t("GHGDatasetSelection.manageVersions.label")}
					</Button>
					<OverlayTrigger placement="top" overlay={<Tooltip>{t("GHGDatasetSelection.export.label")}</Tooltip>}>
						<Button
							variant="outline-primary"
							onClick={handleExport}
							data-testid="export-button"
							disabled={!selectedVersion}
						>
							<BiDownload />
						</Button>
					</OverlayTrigger>
					<OverlayTrigger placement="top" overlay={<Tooltip>{t("GHGDatasetSelection.import.label")}</Tooltip>}>
						<Button
							variant="outline-primary"
							onClick={handleImportClick}
							data-testid="import-button"
							disabled={!selectedVersion}
						>
							<BiUpload />
						</Button>
					</OverlayTrigger>
					<Button
						variant="primary"
						onClick={openAddModal}
						data-testid="add-button"
						disabled={!selectedVersion}
					>
						{t("Uom.addNew.label") || "Add New"}
					</Button>
					<input
						ref={fileInputRef}
						type="file"
						accept=".xlsx,.xls"
						onChange={handleFileSelect}
						style={{ display: "none" }}
						data-testid="file-input"
					/>
				</div>
			</div>

			<div className="table-responsive">
				<Table bordered hover size="sm">
					<thead>
						<tr>
							<th>{t("Input.label") || "Input"}</th>
							<th>{t("Output.label") || "Output"}</th>
							<th>{t("Geography.label") || "Geography"}</th>
							<th>{t("Multiple.label") || "Multiple"}</th>
							<th style={{ width: 60 }}></th>
						</tr>
					</thead>
					<tbody>
						{filteredRows.length === 0 ? (
							<tr>
								<td colSpan={4}>{t("noDataAvailable.label") || "No data available"}</td>
							</tr>
						) : (
							filteredRows.map((row, idx) => (
								<tr key={row.id || idx}>
									<td>{row.inputUnitName || row.inputUnit}</td>
									<td>{row.outputUnitName || row.outputUnit}</td>
									<td>{row.geography || "-"}</td>
									<td>{row.multiple}</td>
									<td className="text-center">
										<Button
											variant="outline-primary"
											size="sm"
											onClick={() => openEditModal(row)}
											aria-label="Edit"
										>
											<BiPencil />
										</Button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</Table>
			</div>

			<UomFormModal
				show={showModal}
				onHide={closeModal}
				isAdd={isAdd}
				form={form}
				errors={errors}
				onChange={onChange}
				onSave={saveRow}
			/>
		</div>
	);
};

export default withTranslation()(UomContainer);

